/**
 * 전역 셀 툴팁 — `.truncate` 클래스 또는 `[data-cell-tooltip]` 가 있는 element 위에
 * 마우스 hover 시, 잘렸을 때만 (scrollWidth > clientWidth) 전체 내용을 portal 툴팁으로 표시.
 *
 * App 루트에 단 한 번만 mount.  페이지/표/모달 별 변경 없이 모든 truncate cell 에 적용됨.
 *
 * 동작:
 *   - document mouseover → 가장 가까운 .truncate / [data-cell-tooltip] 요소 찾기
 *   - scrollWidth > clientWidth 일 때만 표시
 *   - 텍스트 출처: title 속성 우선, 없으면 textContent
 *   - mouseout (요소 밖으로) → 숨김
 *   - 스크롤/리사이즈 → 숨김
 *
 * native title 과의 공존: 1초 후 native tooltip 도 떠도 텍스트 동일 → 노이즈 미미.
 * 빠른 마우스 이동 시엔 native 가 안 뜸.
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface TooltipState {
  text: string;
  x: number;
  y: number;
}

const SELECTOR = '.truncate, [data-cell-tooltip]';

export function CellTooltipMount() {
  const [state, setState] = useState<TooltipState | null>(null);

  useEffect(() => {
    let lastTarget: HTMLElement | null = null;

    function pickTarget(t: EventTarget | null): HTMLElement | null {
      if (!(t instanceof HTMLElement)) return null;
      return (t.closest(SELECTOR) as HTMLElement | null) ?? null;
    }

    function onOver(e: MouseEvent) {
      const target = pickTarget(e.target);
      if (!target) {
        if (lastTarget !== null) {
          lastTarget = null;
          setState(null);
        }
        return;
      }
      if (target === lastTarget) return; // 같은 요소 안에서의 이동 무시
      lastTarget = target;

      if (target.scrollWidth <= target.clientWidth + 1) {
        // 안 잘림 → 표시 X. (브라우저 sub-pixel 오차 보정 +1)
        setState(null);
        return;
      }

      const text = (target.getAttribute('title') ?? target.textContent ?? '').trim();
      if (!text) {
        setState(null);
        return;
      }

      const rect = target.getBoundingClientRect();
      // 화면 우측 너머로 가지 않게 — left clamp
      const tooltipMaxW = 480;
      const padding = 8;
      let x = rect.left;
      const vw = window.innerWidth;
      if (x + tooltipMaxW > vw - padding) x = vw - tooltipMaxW - padding;
      if (x < padding) x = padding;
      setState({ text, x, y: rect.bottom + 4 });
    }

    function onOut(e: MouseEvent) {
      const related = pickTarget(e.relatedTarget);
      if (!related) {
        lastTarget = null;
        setState(null);
      }
    }

    function close() {
      lastTarget = null;
      setState(null);
    }

    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, []);

  if (!state) return null;

  return createPortal(
    <div
      className="fixed z-[60] bg-blue-50 border border-blue-400 text-blue-900 px-2.5 py-1.5 rounded text-xs shadow-lg pointer-events-none whitespace-normal break-words font-medium"
      style={{ left: state.x, top: state.y, maxWidth: 480 }}
    >
      {state.text}
    </div>,
    document.body,
  );
}
